import { map, contains, omit, chain, isEmpty } from 'underscore';

import { getPathFromUrl } from '../address-util';

// this is used to show facebook photos in select dialog
export const getThumbnailUrlForFacebookPhoto = facebookPhoto => facebookPhoto.Picture;

export const getUrlFromStockImage = stockImage => stockImage.externalSrc;

export const getLocalImageData = image => image.imageData;

export const getUniqueFacebookPhotos = (photos) => {
  if (isEmpty(photos)) {
    return [];
  }

  const addedPicture = [];
  const finalPhoto = [];

  photos.forEach((photo) => {
    const photoPath = getPathFromUrl(photo.Picture);
    if (addedPicture.indexOf(photoPath) === -1) {
      finalPhoto.push(photo);
      addedPicture.push(photoPath);
    }
  });

  return finalPhoto;
};

export const StockImageSourceType = {
  M365: 'M365TextAnalysis',
  TemplateAds: 'Template',
};

export const getStockImageThumbnailUrl = (image) => {
  let url = image.externalSrc;

  if (image.source === StockImageSourceType.M365) {
    url = url.replace('.jpg', '');
    return `${url}_thumbnail.jpg`;
  }

  return url;
};

export const getStockImageOriginalUrl = (image) => {
  if (image.source === StockImageSourceType.M365) {
    return image.externalSrc.replace('_thumbnail', '');
  }
  return image.externalSrc;
};

// when uploading, use high resolution one
export const getFacebookPhotoUrlToUpload = (photo) => {
  if (photo.Images && photo.Images[0]) {
    return photo.Images[0].Url;
  }
  return '';
};

// Used to get small version image src from pivot image editor, including local image
export const getImageThumbnailSrcFromImage = (image) => {
  const externalUrl = getStockImageThumbnailUrl(image);
  const fbUrl = getThumbnailUrlForFacebookPhoto(image);
  const localSrc = getLocalImageData(image);

  return externalUrl || fbUrl || localSrc;
};

export const getUploadUrlFromImage = (image) => {
  const externalUrl = getUrlFromStockImage(image);
  const fbUrl = getFacebookPhotoUrlToUpload(image);

  return externalUrl || fbUrl || '';
};

export const ImageSource = {
  Local: 'Local',
  Facebook: 'Facebook',
  StockImage: 'StockImage',
};

/**
 * @param {object} image
 *  image item that contains info of itself including url to upload or image data
 * @return {object} {
 *  url: for external link,
 *  imageData: for local image file,
 *  source: where this image comes from
 * }
 */
export const getUploadDetailFromImage = (image) => {
  if (image.file) {
    return {
      imageData: image.imageData,
      source: ImageSource.Local,
    };
  }

  let url = getUrlFromStockImage(image);
  let source = ImageSource.StockImage;

  if (getFacebookPhotoUrlToUpload(image)) {
    url = getFacebookPhotoUrlToUpload(image);
    source = ImageSource.Facebook;
  }

  return {
    url,
    source,
  };
};

// after uploading
export const formatUploadedFacebookPhoto =
  (uploadedResult, originalFacebookPhoto) => ({
    ...uploadedResult,
    fbImageUrl: getThumbnailUrlForFacebookPhoto(originalFacebookPhoto),
  });

export const formatUploadedStockImage =
  (uploadedResult, originalStockImage) => ({
    ...uploadedResult,
    externalSrc: originalStockImage.externalSrc,
  });

export const formateUploadedPhoto =
  (uploadedResult, originalImage) => (
    originalImage.externalSrc
      ? formatUploadedStockImage(uploadedResult, originalImage)
      : formatUploadedFacebookPhoto(uploadedResult, originalImage)
  );

const getUrlHelpersByType = (type) => {
  let urlPropertyName = 'fbImageUrl';
  let getUrlFromImage = getThumbnailUrlForFacebookPhoto;

  if (type === ImageSource.StockImage) {
    urlPropertyName = 'externalSrc';
    getUrlFromImage = getUrlFromStockImage;
  }

  return {
    urlPropertyName,
    getUrlFromImage,
  };
};

export const filterImportedImages = (images, addedImages, type) => {
  const { urlPropertyName, getUrlFromImage } = getUrlHelpersByType(type);

  const imported = chain(addedImages)
    .pluck(urlPropertyName)
    .map(getPathFromUrl)
    .value();

  return images.filter((image) => {
    const url = getPathFromUrl(getUrlFromImage(image));
    return !contains(imported, url);
  });
};

export const disableImportedImages = (images, addedImages, type) => {
  const { urlPropertyName, getUrlFromImage } = getUrlHelpersByType(type);

  const imported = chain(addedImages)
    .pluck(urlPropertyName)
    .map(getPathFromUrl)
    .value();

  return images.map((image) => {
    const url = getPathFromUrl(getUrlFromImage(image));
    return {
      ...image,
      isDisabled: contains(imported, url),
    };
  });
};

export const formatImagesToSiteConfigImages = images => map(
  images,
  image => omit(image, 'fbImageUrl', 'externalSrc')
);

export const formatImagesToSiteConfigComponentConfig = images => map(images, ({
  IdString,
  fbImageUrl,
  externalSrc,
}) => (
  fbImageUrl
    ? { imageId: IdString, fbImageUrl }
    : { imageId: IdString, externalSrc }
));
